import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { has, cloneDeep } from 'lodash';

import * as Auth from './auth';
import { USER_PATHS } from '../user/user';
import { ADFSStorage, ADFSSettings, AUTH_PATHS, AuthDataInterface, LoginWithCode } from './auth';
import { SocketService } from '../../services/socket.service';
import { tap } from 'rxjs/operators';
import { CustomHttpResponse } from '../../classes/http';
import { environment } from '../../../environments/environment';
import { isLegacyLoginEnabled } from '../../../environments/legacy-login';
import { AuthGroupTypesEnum } from './auth-group-types.enum';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public authDataLoading = false;
  public _authData: BehaviorSubject<AuthDataInterface>;
  public authData: AuthDataInterface = {
    isLoggedIn: false,
    days_left: 0,
    permissions: [],
    groups: [],
    employee: { first_name: '', last_name: '', patronymic_name: '' },
    current_group: { id: 0, description: '', name: '' },
    u_id: null,
    is_privileged: false,
    is_brigadier: false,
    session_id: '',
    loggedByMobileId: false,
  };
  public isExistCSRFToken = false;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router,
    private socketService: SocketService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this._authData = new BehaviorSubject(this.authData);
  }

  public loginWithCode(code: string): Observable<CustomHttpResponse<AuthDataInterface>> {
    return this.http
      .post<CustomHttpResponse<AuthDataInterface>>(`/auth/login`, {
        grant_type: 'authorization_code',
        authorization_code: code,
        role: 4,
      } as LoginWithCode)
      .pipe(
        tap(
          (response) => {
            this.setAuthData(Object.assign({ isLoggedIn: true, loggedByMobileId: true }, cloneDeep(response['data'])));
          },
          (error) => {
            if (has(error, 'error.metadata.code') && error.error.metadata.code === 100002) {
              this.setAuthData(
                Object.assign(
                  {
                    isLoggedIn: true,
                    loggedByMobileId: true,
                  },
                  cloneDeep(error.error['data'])
                )
              );
            }
          }
        )
      );
  }

  public is(authGroupType: AuthGroupTypesEnum): boolean {
    return this.getAuthData() && this.getAuthData().current_group['id'] === authGroupType;
  }

  public isGRSUser(): boolean {
    return this.is(AuthGroupTypesEnum.DispatcherGRS) || this.is(AuthGroupTypesEnum.ServiceEngineerGRS);
  }

  public login(loginData: Auth.Login): Observable<CustomHttpResponse<AuthDataInterface>> {
    return this.http.post<CustomHttpResponse<AuthDataInterface>>(`/auth/login`, loginData).pipe(
      tap(
        (response) => {
          this.setAuthData(Object.assign({ isLoggedIn: true, loggedByMobileId: false }, cloneDeep(response['data'])));
        },
        (error) => {
          if (has(error, 'error.metadata.code') && error.error.metadata.code === 100002) {
            this.setAuthData(
              Object.assign(
                {
                  isLoggedIn: true,
                  loggedByMobileId: false,
                },
                cloneDeep(error.error['data'])
              )
            );
          }
        },
        () => {}
      )
    );
  }

  public logout(): Observable<CustomHttpResponse<any>> {
    return this.http.post<CustomHttpResponse<any>>(`/auth/logout`, {}).pipe(
      tap((response) => {
        this.resetAuthData();
        this.cookieService.delete('csrftoken');
        this.setAuthData(Object.assign({ isLoggedIn: false }, this.authData));
      })
    );
  }

  public openIDLogin() {
    const originUrl = this.document.location.origin;
    const production = [
      originUrl.includes('beekeeper.beeline'),
      originUrl.includes('esas-prod'),
      originUrl.includes('beekeeper-esas.beeline'),
    ].some(Boolean);
    // const redirectUrl = originUrl.includes('ftst3.fttb') ? originUrl.replace('.fttb', '') : originUrl;
    const landscape = production ? 'stsmid' : 'ststst';
    const authUrl = `https://${landscape}.beeline.ru/adfs/oauth2/authorize`;

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: production ? environment.productionClientId : environment.developmentClientId,
      redirect_uri: `${originUrl}/auth/login/callback`,
      state: 'web',
      domain: 'VIMPELCOM_MAIN',
      scope: 'openid',
      'client-request-id': String(Math.random()).slice(2),
    });

    this.document.location.href = `${authUrl}/?${decodeURIComponent(params.toString())}`;
  }

  public logoutRedirect() {
    this.router.navigate(['/' + (isLegacyLoginEnabled() ? AUTH_PATHS.login_legacy : AUTH_PATHS.login)]);
  }

  public checkAuth() {
    return new Promise((resolve, reject) => {
      if (!this.getAuthKey('isLoggedIn') && this.checkCSRFToken()) {
        this.authDataLoading = true;
        this.checkAuthUser().subscribe(
          (response) => {
            // this.authData = Object.assign({isLoggedIn: true}, response['data']);
            this.setAuthData(Object.assign({ isLoggedIn: true }, response['data']));

            resolve({
              auth: true,
              expiriesPassword: this.checkExpiriesPassword(),
              redirect: ['/' + USER_PATHS.change_password_auth_user],
            });

            this.socketService.connect();
            this.authDataLoading = false;
          },
          (error) => {
            reject({
              auth: false,
              passwordExpiries: false,
              redirect: ['/' + AUTH_PATHS.login],
            });
            this.resetAuthData();
            this.logoutRedirect();
            this.authDataLoading = false;
          }
        );
      }

      if (this.getAuthKey('isLoggedIn') && !this.checkCSRFToken()) {
        this.resetAuthData();
        this.logoutRedirect();
        reject({
          auth: false,
          expiriesPassword: false,
          redirect: ['/' + AUTH_PATHS.login],
        });
      }

      if (!this.getAuthKey('isLoggedIn') && !this.checkCSRFToken()) {
        this.resetAuthData();
        this.logoutRedirect();
        reject({
          auth: false,
          expiriesPassword: false,
          redirect: ['/' + AUTH_PATHS.login],
        });
      }
    });
  }

  public sendEmail(sendEmailData: Auth.Email): Observable<CustomHttpResponse<any>> {
    return this.http.post<CustomHttpResponse<any>>(`/auth/forgot_password`, sendEmailData);
  }

  public changeExpiriesPassword(changeExpiriesPasswordData: Auth.RecoveryPassword): Observable<CustomHttpResponse<any>> {
    return this.http.post<CustomHttpResponse<any>>(`/auth/change_password`, changeExpiriesPasswordData);
  }

  public checkValidToken(token): Observable<CustomHttpResponse<Auth.CheckValidToken>> {
    return this.http.get<CustomHttpResponse<Auth.CheckValidToken>>(`/auth/check_token`, {
      params: { token: token },
    });
  }

  public checkAuthUser(): Observable<CustomHttpResponse<AuthDataInterface>> {
    return this.http.get<CustomHttpResponse<AuthDataInterface>>(`/auth/check_auth`);
  }

  public changeRole(changeRoleData: Auth.ChangeRole): Observable<CustomHttpResponse<any>> {
    return this.http.post<CustomHttpResponse<any>>(`/auth/switch_user_group`, changeRoleData);
  }

  public subscribeAuthData(): Observable<AuthDataInterface> {
    return this._authData.asObservable();
  }

  private getAuthData(): AuthDataInterface {
    return this._authData.getValue();
  }

  public getAuthKey(key: string): any {
    return this.getAuthData()[key];
  }

  private setAuthData(authData: AuthDataInterface): void {
    this._authData.next(authData);
  }

  public isLoggedIn(): boolean {
    return !!(this.getAuthKey('isLoggedIn') && this.checkCSRFToken());
  }

  public checkCSRFToken(): boolean {
    this.isExistCSRFToken = this.cookieService.check('csrftoken');

    return this.isExistCSRFToken;
  }

  public checkExpiriesPassword(): boolean {
    return this.getAuthKey('isLoggedIn') && this.getAuthKey('days_left') === 0;
  }

  public checkPermission(permission: string) {
    return this.getAuthKey('permissions').indexOf(permission) > -1;
  }

  public resetSessionData(): void {
    this.socketService.close();
    this._authData.next(Object.assign({ isLoggedIn: false }, this.authData));
    this.isExistCSRFToken = false;
  }

  public resetAuthData(): void {
    this.socketService.close();
    this.isExistCSRFToken = false;
  }

  public checkUseADFS(): Observable<CustomHttpResponse<ADFSSettings>> {
    return this.http.get<CustomHttpResponse<ADFSSettings>>('/auth/settings');
  }

  public getADFSData(): Partial<ADFSStorage> {
    return localStorage.getItem('adfsData') ? JSON.parse(localStorage.getItem('adfsData')) : {};
  }

  public setADFSData(adfsData: ADFSStorage): void {
    localStorage.setItem('adfsData', JSON.stringify(adfsData));
  }
}
